<script setup lang="ts">
import { Card, Button, Image } from "primevue";
import { computed, ref, watch } from "vue";
import type { Message } from "@/api/javaapi/models/message";
import { getFileUrl, downloadFile } from "@/utils/fileUtils";

const props = defineProps<{
  message: Message;
  initiallyUnread?: boolean;
}>();
const emit = defineEmits<{ (e: "retry-message", message: Message): void; (e: "mark-as-read", messageId: number): void }>();
const attachmentUrls = ref<string[]>([]);

const getDisplayTime = () => {
  const timestamp = props.message.timestamp ?? Date.now(); // Fallback to current time if undefined
  return new Intl.DateTimeFormat("en-US", { 
    month: "short", 
    day: "numeric", 
    hour: "numeric", 
    minute: "numeric", 
    hour12: true, 
    year: new Date(timestamp).getFullYear() < new Date().getFullYear() ? "numeric" : undefined 
  }).format(timestamp);
};

const updateAttachmentUrls = () => {
  attachmentUrls.value = props.message.attachmentIDs?.map((id) => getFileUrl(id)) || [];
};

watch(() => props.message.attachmentIDs, (newIds) => {
  updateAttachmentUrls();
}, { immediate: true, deep: true });

const messageHeader = computed(() => {
  const userName = props.message.sendingUser?.name ?? "";
  const time = getDisplayTime();
  const status = props.message.sender && props.message.sendStatus !== "SENT" 
    ? props.message.sendStatus === "PENDING" ? "Sending..." : "Failed" 
    : "";
  return { userName, time, status };
});

const messageClass = computed(() => {
  const baseClass = props.message.sender ? "self-end" : "self-start";
  let bgClass = props.message.sender 
    ? props.message.sendStatus === "PENDING" ? "bg-blue-100 dark:bg-blue-500" 
    : props.message.sendStatus === "FAILED" ? "bg-orange-200 dark:bg-orange-600" 
    : "bg-blue-300 dark:bg-blue-700" 
    : props.message.readStatus ? "bg-gray-200 dark:bg-gray-600" : "bg-gray-300 dark:bg-gray-700";

  return [baseClass, bgClass, "rounded-2xl text-gray-900 dark:text-white max-w-[70%] break-words"];
});

const headerClass = computed(() => {
  const classes = [
    props.message.sendStatus === "FAILED" ? "text-orange-700 dark:text-orange-400" : "text-gray-700 dark:text-gray-300",
    "mx-4 flex items-center gap-2 text-sm",
  ];
  if (props.initiallyUnread) {
    classes.push("font-bold");
  }
  return classes.join(" ");
});

const containerClass = computed(() => (props.message.sender ? "flex-col items-end" : "flex-col items-start"));
const showRetry = computed(() => props.message.sendStatus === "FAILED");

const retryMessage = () => emit("retry-message", props.message);
</script>

<template>
  <div :class="['flex', containerClass]" :data-message-id="props.message.id">
    <div :class="headerClass">
      <span v-if="messageHeader.userName" class="text-gray-950 dark:text-gray-50">{{ messageHeader.userName }}</span>
      <span>{{ messageHeader.time }}</span>
      <span v-if="messageHeader.status" class="italic">{{ messageHeader.status }}</span>
    </div>
    <Card :class="messageClass">
      <template #content>
        <div v-if="attachmentUrls.length" class="flex flex-wrap gap-2 mb-2">
          <div class="max-w-xs h-48 relative" v-for="(attachmentUrl, index) in attachmentUrls">
            <Image :src="attachmentUrl" :key="index" class="w-full h-48" :alt="`Attachment ${index}`" preview/>
            <Button icon="pi pi-download" class="absolute z-1 right-1 bottom-1" severity="secondary" title="Download" size="small" raised @click="downloadFile(attachmentUrl)"/>
          </div>
        </div>
        <p>{{ props.message.content }}</p>
      </template>
      <template #footer v-if="showRetry">
        <div class="flex items-center gap-2">
          <i class="pi pi-exclamation-triangle text-orange-700 dark:text-orange-400" />
          <Button label="Retry" icon="pi pi-refresh" class="p-button-text p-button-sm" @click="retryMessage" />
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
:deep(.p-card) {
  --p-card-body-padding: 0.5rem 1rem;
}
:deep(.p-image img) {
  object-fit: contain;
  object-position: center;
}
</style>